/**
 * Regelversion 10: lebendigere Wahlen.
 *
 *  - Wahlbeteiligung: Wie viele der Wahlberechtigten wählen gehen, hängt von der Polarisierung, Ereignissen
 *    und vom Wahlkampf ab. Ein guter Wahlkampf mobilisiert Nichtwähler, das bringt zusätzliche Stimmen.
 *  - Wählerwanderung: Nach jeder Wahl lässt sich ablesen, von welchen Parteien und aus dem Lager der
 *    Nichtwähler Stimmen gekommen oder gegangen sind (Berechnung aus den Ergebnissen, nur Anzeige).
 *  - Ereignisse im Wahlkampf: Eine Flut, ein Krieg, eine Affäre: Ein spontanes Ereignis kann genau der
 *    Oppositionspartei Auftrieb geben, deren Programm dazu passt.
 *  - Stimmung und Programm: Oppositionsparteien, deren Programm seit der letzten Wahl besser zur
 *    Stimmung im Land passt, legen zu, andere verlieren.
 *  - Themen aus der Legislatur: Krisen der vergangenen Jahre und das Wahlkampf-Ereignis werden zu
 *    Wahlkampfthemen, zu denen ihr Stellung bezieht.
 * Im Mehrspieler gegeneinander gilt das nicht (dort treten echte Parteien an).
 */
import { S, log } from './state';
import { since } from './version';
import { raceOn } from './race';
import { rnd } from './rng';
import { clamp } from './util';
import { pub } from './opinion';
import { press } from './media';
import { PARTIES, OUTLETS } from '../content/parties';
import { AXN } from '../content/axes';
import { zgMax } from './zeitgeist';
import { DI } from '../content/settings';
import { PA, PN } from './rules';

export const elect10On = (): boolean => since(10) && raceOn();

/** Ein Ereignis oder eine Krise als Wahlkampfthema: betroffenes Thema und zwei Haltungen. */
export const STANCE: Record<string, { ax: string; pro: string; con: string }> = {
  gas: { ax: 'atom', pro: 'Atomkraft und heimisches Gas zurück', con: 'Schneller raus aus Öl und Gas' },
  preise: { ax: 'atom', pro: 'Atomkraft und heimisches Gas zurück', con: 'Schneller raus aus Öl und Gas' },
  bank: { ax: 'steuern', pro: 'Banken und Vermögende zur Kasse', con: 'Finanzplatz stärken, keine neuen Steuern' },
  pandemie: { ax: 'buergerv', pro: 'Gesundheitssystem stärken, eine Kasse für alle', con: 'Freiheit statt neuer Vorschriften' },
  streik: { ax: 'mindestlohn', pro: 'Höhere Löhne und Tarifbindung', con: 'Streiks in der Daseinsvorsorge begrenzen' },
  krieg: { ax: 'sicherheit', pro: 'Bundeswehr massiv stärken', con: 'Diplomatie statt Aufrüstung' },
  flucht: { ax: 'migration', pro: 'Menschen in Not aufnehmen, Integration ordnen', con: 'Grenzen schließen, Zuwanderung begrenzen' },
  zoll: { ax: 'europa', pro: 'Europa wirtschaftlich stärker machen', con: 'Deutsche Interessen zuerst' },
  blackout: { ax: 'innen', pro: 'Mehr Befugnisse für Sicherheitsbehörden', con: 'Netze sichern, Bürgerrechte wahren' },
  anschlag: { ax: 'innen', pro: 'Mehr Befugnisse für Polizei und Dienste', con: 'Besonnen bleiben, Bürgerrechte wahren' },
  flut: { ax: 'klima', pro: 'Klimaschutz und Vorsorge verstärken', con: 'Wiederaufbau ohne neue Auflagen' },
  hitze: { ax: 'klima', pro: 'Klimaschutz und Vorsorge verstärken', con: 'Keine neuen Belastungen' },
  werk: { ax: 'sozial', pro: 'Der Staat rettet Arbeitsplätze', con: 'Strukturwandel nicht aufhalten' },
  schulden: { ax: 'haushalt', pro: 'Investieren statt kaputtsparen', con: 'Sparen und Schulden abbauen' },
};
export const CRISIS_N: Record<string, string> = { gas: 'Gaskrise', bank: 'Bankenkrise', pandemie: 'Pandemie', streik: 'Großstreik', krieg: 'Krieg in Europa', flucht: 'Fluchtbewegung', zoll: 'Handelskrieg', blackout: 'Cyberangriff', flut: 'Jahrhundertflut' };

type CampEv = { id: string; t: string; s: string; ax?: string; dir?: number; kind?: 'gov' | 'oppneg' };
/** Ereignisse kurz vor der Wahl. `ax`/`dir`: Welche Haltung profitiert (begünstigt die passendste Oppositionspartei). */
export const CAMP_EVENTS: CampEv[] = [
  { id: 'flut', t: 'Jahrhunderthochwasser', s: 'Wenige Wochen vor der Wahl stehen ganze Landstriche unter Wasser. Klimaschutz ist plötzlich wieder das wichtigste Thema.', ax: 'klima', dir: 1 },
  { id: 'hitze', t: 'Dürresommer und Waldbrände', s: 'Rekordhitze, brennende Wälder, leere Flüsse. Die Debatte über Klimaschutz kocht hoch.', ax: 'klima', dir: 1 },
  { id: 'krieg', t: 'Neuer Krieg in Europas Nachbarschaft', s: 'Ein Nachbarland der EU wird angegriffen. Viele Menschen fragen sich, ob Deutschland verteidigungsfähig ist.', ax: 'sicherheit', dir: 1 },
  { id: 'flucht', t: 'Fluchtbewegung nach Europa', s: 'Hunderttausende sind auf dem Weg nach Europa. Die Kommunen schlagen Alarm.', ax: 'migration', dir: -1 },
  { id: 'anschlag', t: 'Anschlag in einer Großstadt', s: 'Ein Anschlag erschüttert das Land. Der Ruf nach mehr Sicherheit und weniger Zuwanderung wird lauter.', ax: 'migration', dir: -1 },
  { id: 'bank', t: 'Pleite einer Großbank', s: 'Eine Großbank wankt, Sparer bangen um ihr Geld. Viele fordern, Banken und Vermögende stärker zur Kasse zu bitten.', ax: 'sozial', dir: 1 },
  { id: 'werk', t: 'Autobauer schließt Werke', s: 'Zehntausende Arbeitsplätze stehen auf dem Spiel. Die Sorge um soziale Sicherheit wächst.', ax: 'sozial', dir: 1 },
  { id: 'preise', t: 'Energiepreise explodieren', s: 'Strom und Gas kosten plötzlich doppelt so viel. Klimaschutz gerät unter Druck.', ax: 'klima', dir: -1 },
  { id: 'schulden', t: 'Ratingagentur stuft Deutschland herab', s: 'Die Kreditwürdigkeit sinkt, die Zinsen steigen. Sparen wird zum großen Thema.', ax: 'haushalt', dir: -1 },
  { id: 'skandal', t: 'Affäre im Kanzleramt', s: 'Interne Mails legen nahe, dass im Kanzleramt Akten zurückgehalten wurden. Die Opposition wittert ihre Chance.', kind: 'gov' },
  { id: 'oppskandal', t: 'Spendenaffäre in der Opposition', s: 'Verdeckte Großspenden bringen eine Oppositionspartei in Erklärungsnot.', kind: 'oppneg' },
];

const oppIn = (): string[] => (S.res?.inP || []).filter((k: string) => k !== 'YOU' && !S.partners.includes(k) && PARTIES[k]);

/** Wie gut passt das Programm einer Partei zur Stimmung `p` (0–1)? */
function fitOf(pid: string, p: (a: string) => number): number {
  const P = PARTIES[pid]; if (!P) return .5;
  const ax = Object.keys(P.pos || {}); if (!ax.length) return .5;
  return 1 - ax.reduce((s, a) => s + Math.abs((P.pos[a] || 0) - p(a)), 0) / ax.length / 4;
}

/** Beim Start eines Wahlkampfs (nicht dem ersten): vielleicht ein Ereignis, das einer Oppositionspartei hilft. */
export function campEvent9(c: any) {
  if (!elect10On() || c.first) return;
  if (rnd() >= [.45, .6, .75][DI()]) return;
  const E = CAMP_EVENTS[Math.floor(rnd() * CAMP_EVENTS.length)], size = (3 + rnd() * 3) * [.8, 1, 1.25][DI()];
  const opp = oppIn().sort((a, b) => (S.res.share[b] || 0) - (S.res.share[a] || 0)); if (!opp.length) return;
  let pid = opp[0], boost = size, pen = 0, s = E.s;
  if (E.ax) {
    pid = opp.slice().sort((a, b) => (PARTIES[b].pos?.[E.ax!] || 0) * E.dir! - (PARTIES[a].pos?.[E.ax!] || 0) * E.dir! || (S.res.share[b] || 0) - (S.res.share[a] || 0))[0];
    const z = (S.zg ??= {}); z[E.ax] = clamp((z[E.ax] || 0) + .35 * E.dir!, -zgMax(), zgMax());
    const y = S.you[E.ax] || 0; if (y * E.dir! < 0) pen = -Math.min(1.5, Math.abs(y) * .6);
  } else if (E.kind === 'gov') { pen = -2; }
  else { pid = opp[Math.min(opp.length - 1, 1)]; boost = -size; pen = .5; s = `Verdeckte Großspenden bringen ${PN(pid)} in Erklärungsnot.`; }
  c.ev = { id: E.id, t: E.t, s, pid, boost: Math.round(boost * 10) / 10, pen: Math.round(pen * 10) / 10, ax: E.ax || null, dir: E.dir || 0 };
  log(`Ereignis im Wahlkampf: ${E.t}. ${boost > 0 ? `Rückenwind in den Umfragen für ${PN(pid)}.` : `${PN(pid)} verliert in den Umfragen.`}`, boost > 0 ? 'bad' : 'good');
  press(OUTLETS[0].n, `${E.t}: ${boost > 0 ? `Rückenwind für ${PA(pid)}` : `${PA(pid)} unter Druck`}`, pen < 0 ? -.8 : .2);
}

/** Verschiebung der Oppositionsparteien bei der Wahl: Ereignis plus Passung zur Stimmung seit der letzten Wahl. */
export function oppAdj9(c: any): Record<string, number> {
  const adj: Record<string, number> = {}; if (!elect10On() || c.first) return adj;
  const then = S.pubE;
  if (then) for (const k of Object.keys(PARTIES)) {
    if (S.partners.includes(k)) continue;
    const d = fitOf(k, pub) - fitOf(k, (a) => then[a] ?? pub(a));
    if (Math.abs(d) > .002) adj[k] = clamp(d * 40, -3, 3);
  }
  if (c.ev?.pid) adj[c.ev.pid] = (adj[c.ev.pid] || 0) + c.ev.boost;
  return adj;
}

/** Themen aus der Legislatur: Krisen dieser Legislatur und das Wahlkampf-Ereignis, je zwei Haltungen. */
export function eventThemes9(c: any): any[] {
  if (!elect10On() || c.first) return [];
  const ids: { id: string; n: string }[] = [];
  if (c.ev && STANCE[c.ev.id]) ids.push({ id: c.ev.id, n: c.ev.t });
  for (const x of (S.crisisLog || []).filter((x: any) => x.leg === S.leg).slice(-3).reverse())
    if (STANCE[x.id] && !ids.some((i) => STANCE[i.id].ax === STANCE[x.id].ax)) ids.push({ id: x.id, n: CRISIS_N[x.id] || x.id });
  return ids.slice(0, 2).flatMap(({ id, n }) => [1, -1].map((s) => ({ id: `ev:${id}:${s}`, n: `${n}: ${s > 0 ? STANCE[id].pro : STANCE[id].con}`, ev: id, evn: n, ax: STANCE[id].ax, s })));
}

/** Wert eines Ereignis-Themas: passt die Haltung zur Stimmung, ist sie glaubwürdig? */
export function eventThemeVal(id: string): number {
  const [, ev, s0] = id.split(':'), s = Number(s0), St = STANCE[ev]; if (!St) return 0;
  const a = St.ax, fit = 1 - Math.abs(1.5 * s - pub(a)) / 3, y = S.you[a] || 0;
  let v = (fit - .5) * 2;
  if (Math.abs(y) >= 1 && Math.sign(y) !== s) v -= .7; // passt nicht zum eigenen Programm
  return clamp(v, -1.5, 1.4);
}
export const isEvTheme = (id: string) => id.startsWith('ev:');
export const evPair = (id: string) => { const [p, e, s] = id.split(':'); return `${p}:${e}:${-Number(s)}`; };
export const stanceAxisName = (ax: string) => AXN[ax] || ax;

// ── Wahlbeteiligung ──

/** Wahlberechtigte in Mio (aus der Bevölkerung, wenn vorhanden). */
export const eligible = (): number => Math.round((S.pop?.n ? S.pop.n * .715 : 60.4) * 10) / 10;

/**
 * Wahlbeteiligung und Mobilisierung. `q`: Stärke des eigenen Wahlkampfs (Themen, Tour, Duell, Werbung).
 * Gibt die Beteiligung, Nichtwähler, selbst mobilisierte Nichtwähler und den Stimmeneffekt zurück:
 * Ein durchschnittlicher Wahlkampf (rund 0,7 Mio) bringt nichts extra, ein starker bis gut einen Punkt, ein schwacher kostet.
 */
export function turnout9(q: number, first: boolean, c: any) {
  const elig = eligible(), prev = (S.res as any)?.turnout?.t;
  const base = first || prev == null ? 76.5 : prev * .5 + 76.5 * .5;
  const polar = Math.min(4, ((S as any).polar || 0) * .05), ev = c.ev ? 1.2 : 0;
  const mob = clamp(.2 + q * .22, 0, 1.8);
  const t = clamp(base + polar + ev + (mob - .5) / elig * 100, 66, 89);
  const voters = elig * t / 100;
  return { t: Math.round(t * 10) / 10, elig, nv: Math.round((elig - voters) * 100) / 100, mob: Math.round(mob * 100) / 100, bonus: (mob - .7) / voters * 100 * .6, prevT: prev ?? null };
}

export type FlowMatrix = { ids: string[]; prev: number[]; next: number[]; m: number[][] };

/**
 * Wählerwanderung als Matrix (Mio Stimmen, Zeile = Wahl davor, Spalte = neue Wahl, „NV“ = Nichtwähler).
 * Eine Schätzung wie bei echten Wahlen: Die meisten bleiben ihrer Partei treu. Wer wechselt, wechselt eher
 * zu Parteien mit ähnlichem Programm. Gerechnet wird so, dass die Ergebnisse beider Wahlen exakt aufgehen.
 * Nur Anzeige, ohne Einfluss auf das Spiel.
 */
export function flowMatrix(prev: any, next: any): FlowMatrix | null {
  if (!prev?.turnout || !next?.turnout) return null;
  const E = next.turnout.elig, vp = E * prev.turnout.t / 100, vn = E * next.turnout.t / 100; // gleiche Wählerschaft, damit es aufgeht
  const ids = [...new Set([...Object.keys(prev.share), ...Object.keys(next.share)])].filter((k) => (prev.share[k] || 0) + (next.share[k] || 0) >= 1)
    .sort((a, b) => (next.share[b] || 0) - (next.share[a] || 0));
  ids.push('NV');
  const P = ids.map((k) => (k === 'NV' ? E - vp : (prev.share[k] || 0) / 100 * vp));
  const N = ids.map((k) => (k === 'NV' ? E - vn : (next.share[k] || 0) / 100 * vn));
  // Rest durch Rundungen (Parteien unter 1 %) den Nichtwählern zuschlagen
  const nv = ids.length - 1; P[nv] += E - P.reduce((a, b) => a + b, 0); N[nv] += E - N.reduce((a, b) => a + b, 0);
  const pos = (k: string): Record<string, number> => (k === 'YOU' ? S.you : PARTIES[k]?.pos || {});
  const aff = (a: string, b: string): number => {
    if (a === 'NV' || b === 'NV') return .45;
    const pa = pos(a), pb = pos(b), ax = ['sozial', 'haushalt', 'klima', 'migration', 'sicherheit'];
    const d = ax.reduce((s, x) => s + Math.abs((pa[x] || 0) - (pb[x] || 0)), 0) / ax.length / 4;
    return Math.exp(-4 * d);
  };
  const n = ids.length, m = ids.map(() => ids.map(() => 0)), out: number[] = [], inn: number[] = [];
  for (let i = 0; i < n; i++) { const stay = Math.min(P[i], N[i]) * (ids[i] === 'NV' ? .88 : .8); m[i][i] = stay; out[i] = P[i] - stay; inn[i] = N[i] - stay; }
  const K = ids.map((a, i) => ids.map((b, j) => (i === j ? 0 : aff(a, b))));
  const x = K.map((r) => r.slice());
  for (let it = 0; it < 300; it++) { // Zeilen und Spalten abwechselnd auf die Summen bringen
    for (let i = 0; i < n; i++) { const r = x[i].reduce((a, b) => a + b, 0); for (let j = 0; j < n; j++) x[i][j] = r > 0 ? x[i][j] * out[i] / r : 0; }
    for (let j = 0; j < n; j++) { let c = 0; for (let i = 0; i < n; i++) c += x[i][j]; for (let i = 0; i < n; i++) x[i][j] = c > 0 ? x[i][j] * inn[j] / c : 0; }
  }
  for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) if (i !== j) m[i][j] = x[i][j];
  return { ids, prev: P, next: N, m };
}

/** Wählerwanderung aus Sicht der eigenen Partei (Saldo je Partei, Mio, + = zu euch). */
export function voterFlows(prev: any, next: any): { id: string; n: number }[] {
  const F = flowMatrix(prev, next); if (!F) return [];
  const y = F.ids.indexOf('YOU'); if (y < 0) return [];
  return F.ids.map((id, k) => ({ id, n: Math.round((F.m[k][y] - F.m[y][k]) * 100) / 100 })).filter((x) => x.id !== 'YOU' && Math.abs(x.n) >= .01)
    .sort((a, b) => Math.abs(b.n) - Math.abs(a.n));
}
